import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';

import { AllLabels, InstallationFlowType } from '../../../constants/app.constants';
import { FormTypesData, KeyValueData } from '../../../domains/common.domain';
import { TechnicianDataService } from '../technician-data.service';
import { LookupOptionsData } from '../../jurisdiction.domain';
import { AllDropdownKeys } from '../../jurisdiction.constants';
import { CommonService } from '../../../services/common.service';
import { LocalStorageService } from '../../../services/local-storage.service';
import { EnvironmentService } from '../../../services/environment.service';
import { TechnicianDataWaterHiddenFields } from '../../../interface/config.interface';
import { SectionIdentifier } from 'src/app/+shared/components/review-section/review-section.domain';

@Component({
  selector: 'app-water-connection',
  styleUrls: ['./water-connection.component.scss'],
  templateUrl: './water-connection.component.html',
})
export class TechnicianWaterConnectionComponent implements OnChanges, OnInit {
  @Input() currentFormType: FormTypesData;
  @Input() waterConnectionData: any = null;
  @Input() isReadOnly: boolean = false;
  @Input() isDetailsView: boolean = false;
  @Input() applicationId: boolean = false;

  currentInstallationFlow: string;
  formSubscription: Subscription;
  hiddenFields: TechnicianDataWaterHiddenFields;
  infoBubblesData: {
    [key: string]: boolean;
  };
  installationFlowType: KeyValueData;
  peakFlowRequired: boolean;
  showPeakFlowConfirmationCheckboxes: boolean;
  unitLabels: KeyValueData;
  waterHeatingType: LookupOptionsData[];
  waterHeatingTypeKey: string;
  waterConnectionFG: FormGroup;

  isSwknFirewaterPeakflowRequired: boolean;
  showPeakFlowRateAsVolumeFlowRate: boolean;
  showInfoForCommodities: boolean;
  showPeakFlow: boolean;
  showWaterSupplyInfo: boolean;
  sectionIdentifier = SectionIdentifier;

  constructor(
    private commonService: CommonService,
    private environmentService: EnvironmentService,
    private localStorageService: LocalStorageService,
    private technicianDataService: TechnicianDataService
  ) {}

  ngOnInit(): void {
    this.peakFlowRequired = this.environmentService.peakFlowRequired;
    this.showPeakFlowConfirmationCheckboxes = this.environmentService.showPeakFlowConfirmationCheckboxes;
    this.hiddenFields = this.environmentService.waterHiddenFields;
    this.showInfoForCommodities = this.environmentService.showInfoForCommodities;
    this.currentInstallationFlow = this.localStorageService.getItem('installation_flow');
    this.waterHeatingTypeKey = AllDropdownKeys.HotWaterPrepType;
    this.waterHeatingType = this.commonService.getDropdownData(this.waterHeatingTypeKey);
    this.unitLabels = AllLabels.units;
    this.installationFlowType = InstallationFlowType;
    this.infoBubblesData = this.environmentService.getInfoBubblesData('technicianData') || null;
    this.showPeakFlowRateAsVolumeFlowRate = this.environmentService.showPeakFlowRateAsVolumeFlowRate;
    this.isSwknFirewaterPeakflowRequired = false;
    this.showPeakFlow = !(
      this.environmentService.hidePeakFlowInput && this.currentInstallationFlow === this.installationFlowType.TemporaryConnection
    );
    this.showWaterSupplyInfo = this.infoBubblesData?.showWaterSupplyInfo && !this.showPeakFlow;

    this.prepareForm();
    this.technicianDataService.registerForActionOnFormSubmit.subscribe((value: boolean) => {
      if (value) {
        this.emitFormData();
        this.resetData();
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      (changes && changes.currentFormType && !changes.currentFormType.firstChange) ||
      (changes && changes.baseData && !changes.baseData.firstChange)
    ) {
      this.prepareForm();
    }
  }

  updateFireWaterProperty() {
    if (this.waterConnectionFG.get('swknFirewaterPeakflow')) {
      this.isSwknFirewaterPeakflowRequired = this.waterConnectionFG.get('fireWater')?.value;
      if (this.isSwknFirewaterPeakflowRequired) {
        this.waterConnectionFG.get('swknFirewaterPeakflow').setValidators(Validators.required);
      } else {
        this.waterConnectionFG.get('swknFirewaterPeakflow').clearValidators();
      }
      this.waterConnectionFG.get('swknFirewaterPeakflow').updateValueAndValidity();
    }
  }

  private prepareForm(): void {
    this.waterConnectionFG = new FormGroup({
      id: new FormControl((this.waterConnectionData && this.waterConnectionData.id) || ''),
    });

    if (this.showPeakFlow) {
      this.waterConnectionFG.addControl(
        'peakFlow',
        new FormControl((this.waterConnectionData && this.waterConnectionData.peakFlow) || this.environmentService.defaultPeakFlowValue)
      );

      if (this.peakFlowRequired) {
        this.waterConnectionFG.controls.peakFlow.setValidators(Validators.required);
        this.waterConnectionFG.controls.peakFlow.updateValueAndValidity();
      }
    }
    if (this.currentInstallationFlow === this.installationFlowType.MainsConnection) {
      if (!this.hiddenFields.description) {
        this.waterConnectionFG.addControl(
          'description',
          new FormControl((this.waterConnectionData && this.waterConnectionData.description) || '')
        );
      }
      if (!this.hiddenFields.institutionType) {
        this.waterConnectionFG.addControl(
          'institutionType',
          new FormControl((this.waterConnectionData && this.waterConnectionData.institutionType) || '')
        );
      }

      if (!this.hiddenFields.specialService) {
        this.waterConnectionFG.addControl(
          'specialService',
          new FormControl((this.waterConnectionData && this.waterConnectionData.specialService) || false, [Validators.required])
        );
      }
      if (!this.hiddenFields.fireWater) {
        this.waterConnectionFG.addControl(
          'fireWater',
          new FormControl((this.waterConnectionData && this.waterConnectionData.fireWater) || false, [Validators.required])
        );
      }

      if (!this.hiddenFields.swknParcelSize) {
        this.waterConnectionFG.addControl('swknParcelSize', new FormControl(this?.waterConnectionData.swknParcelSize || ''));
      }

      if (!this.hiddenFields.swknFirewaterPeakflow) {
        this.waterConnectionFG.addControl('swknFirewaterPeakflow', new FormControl(this?.waterConnectionData.swknFirewaterPeakflow || ''));
      }

      if (this.currentFormType.index > 0 && !this.environmentService.showOnlyPeakFlow) {
        this.waterConnectionFG.addControl(
          'singleBillCheckbox',
          new FormControl((this.waterConnectionData && this.waterConnectionData.singleBillCheckbox) || false, [Validators.required])
        );
        this.waterConnectionFG.addControl(
          'waterHeatingType',
          new FormControl((this.waterConnectionData && this.waterConnectionData.waterHeatingType) || '')
        );
      }
    }

    if (this.showPeakFlowConfirmationCheckboxes) {
      this.waterConnectionFG.addControl('rentOfWaterManhole', new FormControl(this.isReadOnly ? true : false, Validators.required));
      this.waterConnectionFG.addControl('confirmFrostProtection', new FormControl(this.isReadOnly ? true : false, Validators.required));
    }
    this.updateFireWaterProperty();
    this.emitFormData();

    this.formSubscription = this.waterConnectionFG.valueChanges.subscribe(() => {
      this.emitFormData();
    });
  }

  private emitFormData(): void {
    this.technicianDataService.registerForComponentFormUpdate.next({
      component: 'waterConnection',
      formData: this.waterConnectionFG,
    });
  }

  private resetData(): void {
    this.waterConnectionData = null;
    this.formSubscription.unsubscribe();
  }
}
